import { escapeAttr } from '@/lib/html';
import { findPropertyTypeByKey } from '@/utils/propertyTypes';

export function useProperties() {
  // ### CONSTANTS
  const FREE_BREAKFAST_BADGE_ID = '1';
  const FREE_WIFI_BADGE_ID = '90';
  const COVID_ICON_BADGE_ID = '301';
  const HOSCAR_BADGE_ID = 'hoscar';
  const CARDINAL_TOP_PROPERTIES = {
    0: 'FIRST',
    1: 'SECOND',
    2: 'THIRD',
    3: 'FOURTH',
    4: 'FIFTH',
  };

  // ### COMPOSABLES
  const { useFormatter } = useFormatters();
  const { useUrl } = useUrls();
  const { t } = useI18n();

  const propertyList = ref([]);

  // ### PRIVATE METHODS
  const getPropertyLink = (property) => {
    const is3PIProperty = property?.type === '3PI';
    const propertyName = property?.name || '';
    const propertyUrl = is3PIProperty
      ? useUrl.get3PIPropertyPageUrl(property?.urlPath)
      : useUrl.getPropertyPageUrl({
        propertyTypeSlug: t(findPropertyTypeByKey(property?.type)?.slug || ''),
        propertyId: property?.id,
        urlFriendlyProperty: property?.urlFriendlyName,
      });
    return propertyName && propertyUrl
      ? `<a href="${escapeAttr(propertyUrl)}" aria-label="${escapeAttr(propertyName)}" rel="noopener"> ${escapeAttr(propertyName)} </a>`
      : '';
  };

  // ### PUBLIC METHODS
  const setPropertyList = function (newPropertyList) {
    propertyList.value = newPropertyList;
  };

  // ### COMPUTED
  const getHostelworldProperties = computed(() => {
    return propertyList.value?.filter((property) => property?.type !== '3PI') || [];
  });

  const getPropertyList = computed(() => {
    return propertyList.value;
  });

  const getFlatListOfIds = computed(() => {
    return getHostelworldProperties.value.map((p) => p.id).join('|');
  });

  const getFlatListOfTop30Properties = computed(() => {
    const initialValues = {
      ids: [],
      private_prices: [],
      dorms_prices: [],
      free_breakfast: [],
      free_wifi: [],
      covid_icon: [],
      hoscar_icon: [],
      esg_label: [],
    };

    return getHostelworldProperties.value.slice(0, 30).reduce((propertyTrackingInfo, p) => {
      const hasAvailableDormRooms = p.hasAvailability && p.sharedMinPrice?.value;
      const hasAvailablePrivateRooms = p.hasAvailability && p.privateMinPrice?.value;

      const badgeIds = new Set(p.badges?.map((badge) => badge.id));
      const hasFreeBreakfast = badgeIds.has(FREE_BREAKFAST_BADGE_ID);
      const hasFreeWifi = badgeIds.has(FREE_WIFI_BADGE_ID);
      const hasCovidIcon = badgeIds.has(COVID_ICON_BADGE_ID);
      const hasHoscar = badgeIds.has(HOSCAR_BADGE_ID);

      const hasSustainability = !!p?.sustainability?.length;

      propertyTrackingInfo.ids.push(p.id);
      propertyTrackingInfo.private_prices.push(hasAvailablePrivateRooms ? p.privateMinPrice?.value : 'NULL');
      propertyTrackingInfo.dorms_prices.push(hasAvailableDormRooms ? p.sharedMinPrice?.value : 'NULL');
      propertyTrackingInfo.free_breakfast.push(hasFreeBreakfast ? 1 : 0);
      propertyTrackingInfo.free_wifi.push(hasFreeWifi ? 1 : 0);
      propertyTrackingInfo.covid_icon.push(hasCovidIcon ? 1 : 0);
      propertyTrackingInfo.hoscar_icon.push(hasHoscar ? 1 : 0);
      propertyTrackingInfo.esg_label.push(hasSustainability ? 1 : 0);

      return propertyTrackingInfo;
    }, initialValues);
  });

  const getFlatListOf6To10Ids = computed(() => {
    return getHostelworldProperties.value
      .map((p) => p.id)
      .slice(5, 10)
      .join('|');
  });

  const getTop5PropertiesForTracking = computed(() => {
    return getHostelworldProperties.value.reduce((prevValue, property, idx) => {
      const minPrice = property?.sharedMinPrice?.value || property?.privateMinPrice?.value;
      prevValue.push(`${idx + 1}|${property.id}|${property?.avgRating}|${minPrice}`);
      return prevValue;
    }, []);
  });

  const getTopRankedPropertiesForPlaceholders = computed(() => {
    if (!Array.isArray(propertyList.value)) {
      return [];
    }

    return propertyList.value.slice(0, Object.keys(CARDINAL_TOP_PROPERTIES).length).map((property, idx) => {
      const propertyName = escapeAttr(property?.name || '');

      const propertyLink = getPropertyLink(property);

      return {
        [`${CARDINAL_TOP_PROPERTIES[idx]}TOPPROPERTYNAME`]: propertyName,
        [`${CARDINAL_TOP_PROPERTIES[idx]}TOPPROPERTYURL`]: propertyLink,
        [`${CARDINAL_TOP_PROPERTIES[idx]}TOPPROPERTYRATING`]: property?.avgRating || '',
        [`${CARDINAL_TOP_PROPERTIES[idx]}TOPPROPERTYREVIEWS`]: property?.numberReviews || '',
      };
    });
  });

  const getTopCheapestPropertiesForPlaceholders = computed(() => {
    if (!Array.isArray(propertyList.value)) {
      return [];
    }

    const getMinValidPrice = (property) => {
      const prices = [property?.sharedMinPrice?.value || 0, property?.privateMinPrice?.value || 0]
        .filter((n) => Number(n) > 0)
        .map(Number);
      return prices.length ? Math.min(...prices) : Number.POSITIVE_INFINITY;
    };

    // Add min price to each property based on shared and private rooms prices
    // Remove properties with no minimum price
    // Sort properties by minimum price
    const propertyListSortedByPrice = [...propertyList.value]
      .map((property) => ({
        ...property,
        minPrice: getMinValidPrice(property),
      }))
      .filter((property) => property?.minPrice !== Number.POSITIVE_INFINITY)
      .sort((a, b) => a.minPrice - b.minPrice);

    return propertyListSortedByPrice?.slice(0, Object.keys(CARDINAL_TOP_PROPERTIES).length).map((property, idx) => {
      const propertyName = escapeAttr(property?.name || '');

      const propertyLink = getPropertyLink(property);

      const propertyMinPriceAmount = property?.minPrice;
      const propertyMinPriceCurrency = property?.sharedMinPrice?.currency || property?.privateMinPrice?.currency;

      const placeholderSuffix = idx > 0 ? `_${idx + 1}` : '';

      return {
        [`CHEAPESTPROPNAME${placeholderSuffix}`]: propertyName,
        [`CHEAPESTPROPLINK${placeholderSuffix}`]: propertyLink,
        [`CHEAPESTPROPPRICE${placeholderSuffix}`]: useFormatter.formatCurrency(propertyMinPriceAmount, propertyMinPriceCurrency),
      };
    });
  });

  return {
    usePropertyList: {
      getPropertyList,
      getHostelworldProperties,
      getFlatListOfIds,
      getFlatListOfTop30Properties,
      getFlatListOf6To10Ids,
      getTop5PropertiesForTracking,
      getTopRankedPropertiesForPlaceholders,
      getTopCheapestPropertiesForPlaceholders,
      setPropertyList,
    },
  };
}
